/** @jsxImportSource @emotion/react */
import { type Theme, useTheme } from '@emotion/react';
import Image from 'next/image';
import { useContext, useState } from 'react';

import useTranslate from '@common/hooks/useTranslate';
import Breadcrumbs from '@stories/atoms/Breadcrumbs';
import ReadMoreButton from '@stories/atoms/ReadMoreButton';
import { PageContext } from '@stories/templates/Context/pageContext';
import { media } from '@stories/theming/settings';

import { sendGAEvent } from '../../../../common/utlis';

import type { Interpolation } from '@emotion/serialize';
import type { TBreadcrumbItem } from '@stories/atoms/Breadcrumbs';

const sizes = '(min-width: 1400px) 87.5rem, 100vw';

const translations = {
  'read more': {
    de_DE: '...weiterlesen'
  }
};
// const locale = 'de_DE';

type IntroductoryTextProps = {
  centered?: boolean;
  copyText?: string;
  copyTextExtended: boolean;
  setCopyTextExtended: (boolean) => void;
};

const IntroductoryText: React.FC<IntroductoryTextProps> = ({
  centered = true,
  copyText,
  copyTextExtended,
  setCopyTextExtended
}) => {
  const translate = useTranslate(translations);

  if (!copyText) {
    return <div css={{ minHeight: '2rem' }} />;
  }
  return (
    <div
      id="category_introductory_text"
      css={(t) => ({
        margin: centered ? 'auto' : 0,
        maxWidth: centered ? t.section.width.extraNarrow : t.section.width.content,
        padding: '1rem 1rem 1.5rem',
        position: 'relative',
        [media('desktop')]: {
          paddingBottom: centered ? 0 : '1.5rem'
        }
      })}
    >
      <div
        css={{
          opacity: '0.8',
          ...(!copyTextExtended && {
            WebkitLineClamp: '3',
            WebkitBoxOrient: 'vertical',
            display: '-webkit-box',
            overflow: 'hidden'
          })
        }}
        dangerouslySetInnerHTML={{ __html: copyText }}
      />
      {!copyTextExtended && (
        <ReadMoreButton
          cssProps={{
            bottom: '1.5rem',
            position: 'absolute',
            right: '1rem',
            [media('desktop')]: {
              bottom: centered ? 0 : '1.5rem'
            }
          }}
          label={translate('read more')}
          onClick={() => {
            setCopyTextExtended(true);
            sendGAEvent({ category: 'read more', action: 'click', label: 'category introductory text' });
          }}
        />
      )}
    </div>
  );
};

type HeaderProps = {
  title: string;
  breadcrumbs?: TBreadcrumbItem[];
  copyText?: string;
  cssProps?: Interpolation<Theme>;
  imageFilename?: string;
};

const Header: React.FC<HeaderProps> = ({ title, breadcrumbs, copyText, cssProps, imageFilename }) => {
  const theme = useTheme();
  const [copyTextExtended, setCopyTextExtended] = useState(false);
  const {
    storeData: { categoryHeroFallbackImage }
  } = useContext(PageContext);

  return (
    <header
      css={(t) => ({
        margin: 'auto',
        maxWidth: t.section.width.regular,
        padding: '0',
        position: 'relative',
        [media('desktop')]: {
          padding: '1rem var(--offset) 0'
        },
        ...(cssProps as object)
      })}
    >
      {breadcrumbs &&
        breadcrumbs
          .slice(0, -1)
          .filter(({ name }) => !name.includes('vomachterhof root'))
          .filter(({ url }) => typeof url === 'string').length > 0 && (
          <div
            css={(t) => ({
              backgroundColor: 'white',
              borderRadius: '6px',
              margin: 'auto',
              maxWidth: t.section.width.regular,
              padding: '1rem',
              zIndex: '1',
              [media('desktopLarge')]: {
                padding: '.75rem 1.25rem',
                position: 'absolute',
                top: '2rem',
                left: '2rem',
                transform: 'scale(0.85)',
                transformOrigin: 'left top'
              }
            })}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs} cssProps={{ transform: 'translateY(-1px)' }} />
          </div>
        )}
      {/*eslint-disable-next-line @next/next/no-img-element*/}
      <div
        css={(t) => ({
          aspectRatio: 16 / 5,
          background: imageFilename ? 'transparent' : t.color.primaryBackground,
          left: 0,
          minHeight: '160px',
          objectFit: 'cover',
          overflow: 'hidden',
          position: 'relative',
          top: 0,
          width: '100%',
          [media('desktop')]: {
            borderRadius: '16px',
            minHeight: '30vh'
          }
        })}
      >
        {(imageFilename || categoryHeroFallbackImage) && (
          <Image
            alt=""
            fill
            src={imageFilename ? `/media/catalog/category/${imageFilename}` : categoryHeroFallbackImage || ''}
            sizes={sizes}
            css={{ objectFit: 'cover' }}
            fetchPriority="high"
          />
        )}
        <h1
          css={{
            ...theme.headline.h1,
            color: 'white',
            left: '50%',
            lineHeight: '90%',
            maxWidth: '35rem',
            position: 'absolute',
            textAlign: 'center',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%'
          }}
        >
          {title}
        </h1>
      </div>
      <IntroductoryText {...{ copyText, copyTextExtended, setCopyTextExtended }} />
    </header>
  );
};

export default Header;
export type { HeaderProps };
